import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Icon,
  Input,
  InputNumber,
  Row,
  Select,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from 'antd';
import moment from 'moment';
import * as echarts from 'echarts';
import { apiPost } from './utils/apiService';
import { getServerDisplayName, getServerType } from './utils/serverConfig';
import { buildIntervalTooltipHtml } from './utils/dgIntervalTooltip';
import { downloadDgReportPdf } from './utils/dgReportPdf';
import { findMeterByType } from './utils/fuelService';
import {
  DEFAULT_ASSUMPTIONS,
  STANDARD_DG_SIZES_KVA,
  buildAssetIntervals,
  buildUtilisationSeries,
  fitFleetFuelCurve,
  hasKwhMeter,
  suggestKvaFromText,
  summariseAsset,
  summarisePortfolio,
} from './utils/dgOperatingLevel';
import {
  ExportableTable,
  downloadCsv,
  fmtL,
  numSorter,
  nzCsv,
  searchProps,
  strSorter,
} from './utils/tableKit';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;

/*
 * DG Operating Level & Capacity Utilisation Report
 * ------------------------------------------------
 * Answers, for every DG in the fleet (an asset that has KWH defined as a meter
 * reading): how hard is it actually working, and is it the right size?
 *
 * DATA SOURCING (read before changing any calculation)
 * ====================================================
 * ONE API call per run: POST /fuel/mileage-report with asset: null over the
 * selected period — the same call the Full Tank Consumption Report makes. Every
 * asset, model, location and meter reading needed is already populated on those
 * entries, so there are no per-asset or per-row follow-up calls. All filtering,
 * grouping and analysis happens on the client over that one payload.
 *
 * The measurement is full tank to full tank, exactly as the other fuel reports
 * define it. The arithmetic lives in src/utils/dgOperatingLevel.js so it can be
 * tested without the UI; this file fetches, lets the user confirm the inputs
 * that AssetPro does not store, and renders.
 *
 * WHAT THE USER MUST SUPPLY (and why it is not defaulted)
 * ======================================================
 *  - Rated kVA per asset model. AssetPro has no capacity field, so the rating
 *    only exists inside model/sub-type text. The report parses a SUGGESTION out
 *    of that text, shows the exact string it came from, and asks for
 *    confirmation. A model left blank is analysed for operating level but is
 *    excluded from every capacity, sizing and saving figure.
 *  - Diesel price and DG purchase prices. These are commercial data with no
 *    defensible default, so they start empty and every money column stays blank
 *    (never zero) until they are entered.
 *
 * SCREENING LIMITATION, repeated on screen: the kW here is the AVERAGE between
 * refuelling events, not an instantaneous peak. Motor starts and step loads can
 * be far higher, so a recommended size is a candidate for a load study, not a
 * purchase decision.
 */

const STORAGE_KEY = () => `aim.dgOperatingLevel.v1.${getServerType()}`;

const pct = (v, dp = 1) =>
  typeof v === 'number' && Number.isFinite(v) ? `${(v * 100).toFixed(dp)}%` : '—';

const fmtNum = (v, dp = 2) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : '—';

const fmtInt = (v) =>
  typeof v === 'number' && Number.isFinite(v) ? Math.round(v).toLocaleString('en-IN') : '—';

// A cell that is blank for a reason shows the reason, so "—" is never mistaken
// for a zero or for an oversight.
const withReason = (value, reason) =>
  value === null || value === undefined ? (
    <Tooltip title={reason || 'Not available'}>
      <span style={{ color: '#bfbfbf' }}>
        —{reason ? <Icon type="info-circle" style={{ marginLeft: 4, fontSize: 11 }} /> : null}
      </span>
    </Tooltip>
  ) : (
    value
  );

// Populated references arrive either as an object or as a bare ObjectId string.
// Only a real name is ever displayed; an id is treated as "no name", never shown
// as if it were one.
const extractName = (field) => {
  if (!field) return null;
  if (typeof field === 'string') {
    return /^[0-9a-fA-F]{24}$/.test(field.trim()) ? null : field;
  }
  if (typeof field === 'object') return field.name || field.code || field.title || null;
  return null;
};

const extractId = (field) => {
  if (!field) return null;
  if (typeof field === 'string') return field;
  if (typeof field === 'object') return field._id || null;
  return null;
};

const DGOperatingLevelReport = () => {
  // --- scope / fetch state --------------------------------------------------
  const [dateRange, setDateRange] = useState([moment().subtract(3, 'months').startOf('day'), moment().endOf('day')]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState(null); // { assets, entriesByAsset, models, rangeStartMs, rangeEndMs, totalEntries, queriedRange }

  // --- user-confirmed inputs ------------------------------------------------
  const [modelKva, setModelKva] = useState({}); // modelKey -> confirmed kVA
  const [assumptions, setAssumptions] = useState(DEFAULT_ASSUMPTIONS);
  const [sizeRows, setSizeRows] = useState(
    STANDARD_DG_SIZES_KVA.map((kva) => ({ kva, available: true, price: null }))
  );
  const [newSize, setNewSize] = useState(null);
  const [dieselPrice, setDieselPrice] = useState(null);

  // --- display filters (applied client-side; they never trigger a refetch) ---
  const [filterLocations, setFilterLocations] = useState([]);
  const [filterSubLocations, setFilterSubLocations] = useState([]);
  const [filterModels, setFilterModels] = useState([]);
  const [assetSearch, setAssetSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const chartRef = useRef(null);
  const chartDivRef = useRef(null);
  const curveChartRef = useRef(null);
  const curveChartDivRef = useRef(null);

  // Confirmed capacities and prices persist per server so they are typed once,
  // not once per session. They are inputs to the analysis, never a data source
  // that could go stale behind the user's back — everything is on screen.
  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY()) || 'null');
      if (!saved) return;
      if (saved.modelKva) setModelKva(saved.modelKva);
      if (saved.dieselPrice !== undefined) setDieselPrice(saved.dieselPrice);
      if (Array.isArray(saved.sizeRows) && saved.sizeRows.length) setSizeRows(saved.sizeRows);
      if (saved.assumptions) setAssumptions({ ...DEFAULT_ASSUMPTIONS, ...saved.assumptions });
    } catch (e) {
      console.warn('DG report: could not read saved inputs', e);
    }
  }, []);

  const persist = useCallback((patch) => {
    try {
      const current = JSON.parse(window.localStorage.getItem(STORAGE_KEY()) || '{}');
      window.localStorage.setItem(STORAGE_KEY(), JSON.stringify({ ...current, ...patch }));
    } catch (e) {
      console.warn('DG report: could not save inputs', e);
    }
  }, []);

  // --- step 1: fetch the period and identify the DGs ------------------------
  const loadScope = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Select a date range first.');
      return;
    }
    setLoading(true);
    setScope(null);
    try {
      const start = dateRange[0].clone().startOf('day');
      const end = dateRange[1].clone().endOf('day');

      const result = await apiPost('/fuel/mileage-report', {
        asset: null,
        location: 'all',
        subLocation: 'all',
        periodFrom: start.toISOString(),
        periodTo: end.toISOString(),
      });

      if (!result.success) {
        message.error(`Failed to fetch fuel entries: ${result.error}`);
        setLoading(false);
        return;
      }

      const entries = result.data || [];

      // Group by asset code, keeping the entry with the most populated
      // references as the descriptive record for that asset.
      const entriesByAsset = {};
      const bestEntry = {};
      entries.forEach((entry) => {
        const assetCode = (entry.asset && entry.asset.assetCode) || entry.assetCode || null;
        if (!assetCode) return;
        if (!entriesByAsset[assetCode]) entriesByAsset[assetCode] = [];
        entriesByAsset[assetCode].push(entry);

        const score =
          (extractName(entry.assetModel) ? 10 : 0) +
          (extractName(entry.assetSubType) ? 10 : 0) +
          (extractName(entry.location) ? 5 : 0) +
          (extractName(entry.subLocation) ? 5 : 0) +
          (extractName(entry.assetType) ? 5 : 0);
        if (!bestEntry[assetCode] || score > bestEntry[assetCode].score) {
          bestEntry[assetCode] = { entry, score };
        }
      });

      // A DG, for this report, is an asset that has a KWH meter on its fuel
      // entries. Decided by the meter's own name — never by asset type, which is
      // free text and differs between clients.
      const assets = [];
      Object.keys(entriesByAsset).forEach((assetCode) => {
        if (!hasKwhMeter(entriesByAsset[assetCode])) return;
        const e = bestEntry[assetCode].entry;
        const modelName = extractName(e.assetModel);
        const modelId = extractId(e.assetModel);
        assets.push({
          assetCode,
          assetId: (e.asset && e.asset._id) || null,
          ownerCode: (e.asset && (e.asset.ownerCode || e.asset.ownercode)) || '',
          altName: (e.asset && (e.asset.altName || e.asset.altname)) || '',
          assetType: extractName(e.assetType),
          assetSubType: extractName(e.assetSubType),
          assetModel: modelName,
          modelKey: modelId || modelName || '__no_model__',
          location: extractName(e.location),
          subLocation: extractName(e.subLocation),
          entryCount: entriesByAsset[assetCode].length,
          hasHoursMeter: entriesByAsset[assetCode].some((x) => findMeterByType(x.mileage, 'HOURS') !== null),
        });
      });

      // One row per asset model — that is the level the rating is confirmed at.
      const modelMap = {};
      assets.forEach((a) => {
        if (!modelMap[a.modelKey]) {
          modelMap[a.modelKey] = {
            modelKey: a.modelKey,
            modelName: a.assetModel,
            subTypeName: a.assetSubType,
            assetCodes: [],
            ownerCodes: [],
          };
        }
        modelMap[a.modelKey].assetCodes.push(a.assetCode);
        if (a.ownerCode) modelMap[a.modelKey].ownerCodes.push({ assetCode: a.assetCode, ownerCode: a.ownerCode });
      });

      const models = Object.values(modelMap).map((m) => {
        // The sub-type is tried before the model name because it is the field
        // that most often carries a clean rating ("600 KVA DG").
        const suggestion = suggestKvaFromText([
          { field: 'Asset sub type', text: m.subTypeName },
          { field: 'Asset model', text: m.modelName },
        ]);
        // Owner codes frequently carry the rating too ("600KVA(M-1)"). They are
        // NOT used as the suggestion — they are per asset, and this rating is
        // confirmed per model — but a disagreement is surfaced, because it means
        // one model row may be covering two different ratings.
        const ownerKva = m.ownerCodes
          .map((o) => ({ ...o, parsed: suggestKvaFromText([{ field: 'Owner code', text: o.ownerCode }]) }))
          .filter((o) => o.parsed);
        return { ...m, assetCount: m.assetCodes.length, suggestion, ownerKva };
      }).sort((a, b) => String(a.modelName || '').localeCompare(String(b.modelName || '')));

      // Pre-fill any model that has no confirmed value yet with its parsed
      // suggestion. Existing confirmations are never overwritten.
      const nextModelKva = { ...modelKva };
      models.forEach((m) => {
        if (nextModelKva[m.modelKey] === undefined && m.suggestion) nextModelKva[m.modelKey] = m.suggestion.value;
      });
      setModelKva(nextModelKva);
      persist({ modelKva: nextModelKva });

      setScope({
        assets: assets.sort((a, b) => a.assetCode.localeCompare(b.assetCode)),
        entriesByAsset,
        models,
        rangeStartMs: start.valueOf(),
        rangeEndMs: end.valueOf(),
        totalEntries: entries.length,
        queriedRange: [start, end],
      });

      if (assets.length === 0) {
        message.warning('No asset in this period has a KWH meter reading, so there is no DG to analyse.');
      } else {
        message.success(`Found ${assets.length} DG(s) with KWH metering across ${entries.length} fuel entries.`);
      }
    } catch (error) {
      console.error('DG report: scope load failed', error);
      message.error('Failed to load the period.');
    } finally {
      setLoading(false);
    }
  };

  // --- filter option lists --------------------------------------------------
  const options = useMemo(() => {
    if (!scope) return { locations: [], subLocations: [], models: [] };
    const uniq = (list) => [...new Set(list.filter(Boolean))].sort();
    return {
      locations: uniq(scope.assets.map((a) => a.location)),
      subLocations: uniq(scope.assets.map((a) => a.subLocation)),
      models: uniq(scope.assets.map((a) => a.assetModel)),
    };
  }, [scope]);

  const activeSizes = useMemo(
    () => sizeRows.filter((r) => r.available && Number.isFinite(Number(r.kva))).map((r) => Number(r.kva)),
    [sizeRows]
  );

  const priceMap = useMemo(() => {
    const map = {};
    sizeRows.forEach((r) => {
      if (r.price !== null && r.price !== undefined && r.price !== '' && Number.isFinite(Number(r.price))) {
        map[String(Number(r.kva))] = Number(r.price);
      }
    });
    return map;
  }, [sizeRows]);

  // --- the analysis ---------------------------------------------------------
  // Derived, not stored: editing an assumption, a rating or a price re-runs the
  // numbers immediately, so what is on screen always matches the inputs above
  // it. No API call is repeated — this works entirely on the fetched payload.
  const analysis = useMemo(() => {
    if (!scope) return null;

    const search = assetSearch.trim().toLowerCase();
    const assets = scope.assets.filter((a) => {
      if (filterLocations.length && !filterLocations.includes(a.location)) return false;
      if (filterSubLocations.length && !filterSubLocations.includes(a.subLocation)) return false;
      if (filterModels.length && !filterModels.includes(a.assetModel)) return false;
      if (search) {
        const hay = `${a.assetCode} ${a.ownerCode} ${a.altName}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });

    const perAsset = assets.map((asset) => {
      const confirmed = modelKva[asset.modelKey];
      const ratedKva =
        confirmed !== undefined && confirmed !== null && confirmed !== '' && Number.isFinite(Number(confirmed))
          ? Number(confirmed)
          : null;
      const { intervals, dropped } = buildAssetIntervals({
        assetCode: asset.assetCode,
        entries: scope.entriesByAsset[asset.assetCode] || [],
        ratedKva,
        assumptions,
        rangeStartMs: scope.rangeStartMs,
        rangeEndMs: scope.rangeEndMs,
      });
      return { asset, ratedKva, intervals, dropped };
    });

    // The fuel curve is fitted ONCE across the whole filtered fleet: a single
    // generator rarely varies its own size, so the size-related litres/hour term
    // is only separable across sets of different ratings.
    const curveObservations = [];
    perAsset.forEach(({ ratedKva, intervals }) => {
      intervals.forEach((iv) => {
        if (iv.validForFuelCurve && ratedKva) {
          curveObservations.push({ kva: ratedKva, avgKw: iv.avgKw, litresPerHour: iv.litresPerHour });
        }
      });
    });
    const curve = fitFleetFuelCurve(curveObservations, assumptions);

    const assetRows = perAsset.map(({ asset, ratedKva, intervals }) =>
      summariseAsset({
        asset,
        intervals,
        ratedKva,
        assumptions,
        curve,
        sizes: activeSizes,
        prices: priceMap,
        dieselPricePerLitre: dieselPrice,
      })
    );

    const portfolio = summarisePortfolio(assetRows, assumptions);

    const allIntervals = perAsset.flatMap(({ asset, intervals }) =>
      intervals.map((iv) => ({
        ...iv,
        assetModel: asset.assetModel,
        location: asset.location,
        subLocation: asset.subLocation,
        ownerCode: asset.ownerCode,
      }))
    );

    // Every dropped interval is counted by its reason so nothing disappears
    // quietly. The reasons carry numbers, so they are keyed by their prefix.
    const dropReasons = {};
    perAsset.forEach(({ dropped }) => {
      dropped.forEach((d) => {
        const key = d.reason.replace(/\d+(\.\d+)?%?/g, 'N');
        if (!dropReasons[key]) dropReasons[key] = { reason: key, count: 0, examples: [] };
        dropReasons[key].count += 1;
        if (dropReasons[key].examples.length < 5) {
          dropReasons[key].examples.push(`${d.assetCode} @ ${moment(d.currentMs).format('DD-MMM-YYYY')}`);
        }
      });
    });

    return {
      assetRows,
      allIntervals,
      curve,
      portfolio,
      dropReasons: Object.values(dropReasons).sort((a, b) => b.count - a.count),
      unconfirmed: assetRows.filter((r) => r.ratedKva === null),
      noIntervals: assetRows.filter((r) => r.intervalCount === 0),
      meterSuspect: assetRows.filter((r) => r.meterSuspect),
      curveObservationCount: curveObservations.length,
      utilisationSeries: buildUtilisationSeries(allIntervals),
    };
  }, [scope, modelKva, assumptions, activeSizes, priceMap, dieselPrice, filterLocations, filterSubLocations, filterModels, assetSearch]);

  // --- charts ---------------------------------------------------------------
  useEffect(() => {
    if (!chartDivRef.current) return undefined;
    if (chartRef.current) {
      chartRef.current.dispose();
      chartRef.current = null;
    }
    const lines = analysis ? analysis.utilisationSeries.series : [];
    if (!lines.length) return undefined;

    // Utilisation over the period: one line per DG, one point per measured
    // interval at its closing full tank. Break points from buildUtilisationSeries
    // become '-' (ECharts' missing value) so a line stops at an excluded interval
    // instead of joining across time that was never measured.
    const chart = echarts.init(chartDivRef.current);
    chart.setOption({
      tooltip: {
        trigger: 'item',
        confine: true,
        // The interval's full working — dates, vouchers, hours, kWh, litres and
        // every division — so a point can be checked without leaving the chart.
        formatter: (p) =>
          p.data && p.data.meta ? buildIntervalTooltipHtml(p.data.meta.interval, assumptions.powerFactor) : '',
      },
      legend: { type: 'scroll', top: 0 },
      grid: { left: 60, right: 30, top: 40, bottom: 70 },
      xAxis: { type: 'time' },
      yAxis: { type: 'value', name: 'Utilisation %', min: 0, axisLabel: { formatter: '{value}%' } },
      dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 10 }],
      series: lines.map((line, index) => ({
        name: line.assetCode,
        type: 'line',
        showSymbol: true,
        symbolSize: 6,
        connectNulls: false,
        data: line.points.map((pt) =>
          pt.isBreak
            ? { value: [pt.ms, '-'] }
            : { value: [pt.ms, Number((pt.utilisation * 100).toFixed(1))], meta: pt }
        ),
        // Rated output reference, drawn once rather than repeated per line.
        ...(index === 0
          ? {
              markLine: {
                silent: true,
                symbol: 'none',
                lineStyle: { color: '#8c8c8c', type: 'dashed' },
                label: { formatter: '100% of rated kW', position: 'insideEndTop' },
                data: [{ yAxis: 100 }],
              },
            }
          : {}),
      })),
    });

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => chart.resize());
      ro.observe(chartDivRef.current);
    }
    chartRef.current = chart;
    return () => {
      if (ro) ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [analysis, assumptions.powerFactor]);

  useEffect(() => {
    if (!curveChartDivRef.current) return undefined;
    if (curveChartRef.current) {
      curveChartRef.current.dispose();
      curveChartRef.current = null;
    }
    if (!analysis || !analysis.curve.ok) return undefined;

    const points = analysis.allIntervals
      .filter((iv) => iv.validForFuelCurve && iv.ratedKva)
      .map((iv) => [
        analysis.curve.capacityCoefficient * iv.ratedKva + analysis.curve.loadCoefficient * iv.avgKw,
        iv.litresPerHour,
        iv.assetCode,
      ]);
    if (!points.length) return undefined;
    const max = Math.max(...points.map((p) => Math.max(p[0], p[1])));

    const chart = echarts.init(curveChartDivRef.current);
    chart.setOption({
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: (p) =>
          `<b>${p.data[2]}</b><br/>Predicted ${p.data[0].toFixed(2)} L/h<br/>Measured ${p.data[1].toFixed(2)} L/h`,
      },
      grid: { left: 70, right: 30, top: 30, bottom: 60 },
      xAxis: { type: 'value', name: 'Predicted L/hour', nameLocation: 'middle', nameGap: 30, max: Math.ceil(max) },
      yAxis: { type: 'value', name: 'Measured L/hour', nameLocation: 'middle', nameGap: 45, max: Math.ceil(max) },
      series: [
        { type: 'scatter', symbolSize: 6, data: points, itemStyle: { color: '#1890ff', opacity: 0.6 } },
        {
          type: 'line',
          data: [[0, 0], [Math.ceil(max), Math.ceil(max)]],
          showSymbol: false,
          lineStyle: { color: '#8c8c8c', type: 'dashed' },
          tooltip: { show: false },
        },
      ],
    });

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => chart.resize());
      ro.observe(curveChartDivRef.current);
    }
    curveChartRef.current = chart;
    return () => {
      if (ro) ro.disconnect();
      if (curveChartRef.current) {
        curveChartRef.current.dispose();
        curveChartRef.current = null;
      }
    };
  }, [analysis]);

  useEffect(
    () => () => {
      if (chartRef.current) chartRef.current.dispose();
      if (curveChartRef.current) curveChartRef.current.dispose();
    },
    []
  );

  // --- input handlers -------------------------------------------------------
  const setModelRating = (modelKey, value) => {
    const next = { ...modelKva };
    if (value === null || value === undefined || value === '') delete next[modelKey];
    else next[modelKey] = value;
    setModelKva(next);
    persist({ modelKva: next });
  };

  const setAssumption = (key, value) => {
    const next = { ...assumptions, [key]: value };
    setAssumptions(next);
    persist({ assumptions: next });
  };

  const setSizeField = (kva, field, value) => {
    const next = sizeRows.map((r) => (r.kva === kva ? { ...r, [field]: value } : r));
    setSizeRows(next);
    persist({ sizeRows: next });
  };

  const addSize = () => {
    const value = Number(newSize);
    if (!Number.isFinite(value) || value <= 0) {
      message.warning('Enter a kVA value greater than zero.');
      return;
    }
    if (sizeRows.some((r) => Number(r.kva) === value)) {
      message.warning(`${value} kVA is already listed.`);
      return;
    }
    const next = [...sizeRows, { kva: value, available: true, price: null }].sort((a, b) => a.kva - b.kva);
    setSizeRows(next);
    persist({ sizeRows: next });
    setNewSize(null);
  };

  // --- columns --------------------------------------------------------------
  // --- PDF export -------------------------------------------------------------
  // Exports exactly what is on screen: the LOADED period (not an unloaded change
  // to the date picker), the report filters above, and the ratings, assumptions
  // and prices entered. No API call — the PDF is built from the analysis in memory.
  const exportPdf = () => {
    if (!analysis || !scope) return;
    setExporting(true);
    // Let the button show its spinner before the synchronous PDF build starts.
    setTimeout(() => {
      try {
        const fileName = downloadDgReportPdf({
          analysis,
          scope,
          filters: {
            locations: filterLocations,
            subLocations: filterSubLocations,
            models: filterModels,
            assetSearch,
          },
          assumptions,
          dieselPrice,
          priceMap,
          modelKva,
          serverLabel: getServerDisplayName(),
        });
        message.success(`Saved ${fileName}`);
      } catch (error) {
        console.error('DG report: PDF export failed', error);
        message.error(`The PDF could not be generated: ${error.message}`);
      } finally {
        setExporting(false);
      }
    }, 50);
  };

  const modelColumns = [
    { title: 'Asset Model', dataIndex: 'modelName', key: 'modelName', width: 220, sorter: strSorter('modelName'), ...searchProps('modelName'), render: (v) => v || <Tag color="orange">No asset model</Tag> },
    { title: 'Asset Sub Type', dataIndex: 'subTypeName', key: 'subTypeName', width: 180, render: (v) => v || '—' },
    { title: 'DGs', dataIndex: 'assetCount', key: 'assetCount', width: 70, align: 'right', sorter: numSorter('assetCount') },
    {
      title: 'Parsed suggestion',
      key: 'suggestion',
      width: 230,
      render: (_, r) =>
        r.suggestion ? (
          <div>
            <b>{r.suggestion.value} kVA</b>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              from {r.suggestion.field}: “{r.suggestion.text}”
            </div>
          </div>
        ) : (
          <Tooltip title="No kVA rating appears in the model or sub-type text. Nothing is assumed — type the rating to include these DGs in the capacity analysis.">
            <Tag color="orange">Not in the name</Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Rated kVA (confirm / correct)',
      key: 'ratedKva',
      width: 210,
      render: (_, r) => (
        <div>
          <InputNumber
            min={0}
            step={1}
            style={{ width: 130 }}
            value={modelKva[r.modelKey]}
            placeholder="not set"
            onChange={(v) => setModelRating(r.modelKey, v)}
          />
          {modelKva[r.modelKey] === undefined || modelKva[r.modelKey] === null ? (
            <div style={{ fontSize: 11, color: '#fa8c16' }}>Excluded from capacity analysis</div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Cross-check',
      key: 'crossCheck',
      render: (_, r) => {
        const confirmed = modelKva[r.modelKey];
        const mismatches = r.ownerKva.filter(
          (o) => confirmed !== undefined && confirmed !== null && Number(o.parsed.value) !== Number(confirmed)
        );
        if (!r.ownerKva.length) return <span style={{ color: '#bfbfbf' }}>No kVA in owner codes</span>;
        if (!mismatches.length) return <Tag color="green">Owner codes agree</Tag>;
        return (
          <Tooltip
            title={`One rating is confirmed for the whole model, but these assets' owner codes read differently: ${mismatches
              .map((m) => `${m.assetCode} → ${m.parsed.value} kVA (“${m.ownerCode}”)`)
              .join('; ')}`}
          >
            <Tag color="red">
              <Icon type="warning" /> {mismatches.length} owner code(s) disagree
            </Tag>
          </Tooltip>
        );
      },
    },
  ];

  const assetColumns = [
    {
      title: 'DG',
      dataIndex: 'assetCode',
      key: 'assetCode',
      fixed: 'left',
      width: 180,
      sorter: strSorter('assetCode'),
      ...searchProps('assetCode', (r) => `${r.assetCode} ${r.ownerCode} ${r.altName}`),
      render: (v, r) => (
        <div>
          <b>{v}</b>
          {r.ownerCode ? <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.ownerCode}</div> : null}
        </div>
      ),
    },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 120, sorter: strSorter('location'), render: (v) => v || '—' },
    { title: 'Model', dataIndex: 'assetModel', key: 'assetModel', width: 160, sorter: strSorter('assetModel'), render: (v) => v || '—' },
    {
      title: 'Rated kVA',
      dataIndex: 'ratedKva',
      key: 'ratedKva',
      width: 100,
      align: 'right',
      sorter: numSorter('ratedKva'),
      render: (v) => withReason(v === null ? null : fmtNum(v, v % 1 === 0 ? 0 : 1), 'Rating not confirmed for this asset model'),
    },
    { title: 'Intervals', dataIndex: 'intervalCount', key: 'intervalCount', width: 90, align: 'right', sorter: numSorter('intervalCount') },
    { title: 'Hours', dataIndex: 'hours', key: 'hours', width: 100, align: 'right', sorter: numSorter('hours'), render: (v) => fmtNum(v, 1) },
    { title: 'Energy kWh', dataIndex: 'energyKwh', key: 'energyKwh', width: 120, align: 'right', sorter: numSorter('energyKwh'), render: fmtInt },
    { title: 'Fuel L', dataIndex: 'fuelL', key: 'fuelL', width: 110, align: 'right', sorter: numSorter('fuelL'), render: fmtInt },
    {
      title: 'Operating Level (avg kW)',
      dataIndex: 'avgKw',
      key: 'avgKw',
      width: 150,
      align: 'right',
      sorter: numSorter('avgKw'),
      render: (v) => withReason(v === null ? null : <b>{fmtNum(v, 1)}</b>, 'No hours run to divide by'),
    },
    {
      title: 'Utilisation',
      dataIndex: 'utilisation',
      key: 'utilisation',
      width: 110,
      align: 'right',
      sorter: numSorter('utilisation'),
      render: (v) =>
        withReason(
          v === null ? null : (
            <b style={{ color: v < 0.3 ? '#f5222d' : v < 0.5 ? '#fa8c16' : '#389e0d' }}>{pct(v)}</b>
          ),
          'Needs a confirmed rated kVA and measured hours'
        ),
    },
    {
      title: 'kWh/L',
      dataIndex: 'efficiencyKwhPerL',
      key: 'efficiencyKwhPerL',
      width: 100,
      align: 'right',
      sorter: numSorter('efficiencyKwhPerL'),
      render: (v, r) =>
        withReason(
          v === null ? null : (
            <span style={{ color: r.meterSuspect ? '#f5222d' : undefined }}>
              {fmtNum(v)} {r.meterSuspect ? <Icon type="warning" /> : null}
            </span>
          ),
          'No fuel recorded'
        ),
    },
    { title: 'P95 kW', dataIndex: 'p95Kw', key: 'p95Kw', width: 100, align: 'right', sorter: numSorter('p95Kw'), render: (v) => withReason(v === null ? null : fmtNum(v, 1), 'No valid interval') },
    { title: 'Required kVA', dataIndex: 'requiredKva', key: 'requiredKva', width: 120, align: 'right', sorter: numSorter('requiredKva'), render: (v) => withReason(v === null ? null : fmtNum(v, 1), 'No P95 load') },
    {
      title: 'Candidate kVA',
      dataIndex: 'recommendedKva',
      key: 'recommendedKva',
      width: 130,
      align: 'right',
      sorter: numSorter('recommendedKva'),
      render: (v, r) =>
        withReason(
          v === null ? null : (
            <span>
              <b>{fmtNum(v, v % 1 === 0 ? 0 : 1)}</b>
              {r.p95LoadingAfter !== null ? (
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>P95 at {pct(r.p95LoadingAfter, 0)}</div>
              ) : null}
            </span>
          ),
          r.recommendationReason
        ),
    },
    {
      title: 'Period saving L',
      dataIndex: 'periodSavingL',
      key: 'periodSavingL',
      width: 130,
      align: 'right',
      sorter: numSorter('periodSavingL'),
      render: (v, r) => withReason(v === null ? null : fmtInt(v), r.savingReason),
    },
    {
      title: 'Annualised L',
      dataIndex: 'annualisedSavingL',
      key: 'annualisedSavingL',
      width: 130,
      align: 'right',
      sorter: numSorter('annualisedSavingL'),
      render: (v, r) => withReason(v === null ? null : fmtInt(v), r.annualisationReason),
    },
    {
      title: 'Replacement ₹',
      dataIndex: 'replacementCostInr',
      key: 'replacementCostInr',
      width: 130,
      align: 'right',
      sorter: numSorter('replacementCostInr'),
      render: (v, r) =>
        withReason(
          v === null ? null : fmtInt(v),
          r.recommendedKva && r.ratedKva && r.recommendedKva < r.ratedKva
            ? `No price entered for ${r.recommendedKva} kVA`
            : 'No replacement proposed'
        ),
    },
    {
      title: 'Annual fuel value ₹',
      dataIndex: 'annualFuelValueInr',
      key: 'annualFuelValueInr',
      width: 150,
      align: 'right',
      sorter: numSorter('annualFuelValueInr'),
      render: (v) => withReason(v === null ? null : fmtInt(v), 'Needs an annualised saving and a diesel price'),
    },
    {
      title: 'Payback months',
      dataIndex: 'paybackMonths',
      key: 'paybackMonths',
      width: 130,
      align: 'right',
      sorter: numSorter('paybackMonths'),
      render: (v) => withReason(v === null ? null : fmtNum(v, 1), 'Needs both a replacement price and an annual fuel value'),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 210,
      render: (v, r) => (
        <div>
          <div>{v}</div>
          {r.notes.map((n) => (
            <div key={n} style={{ fontSize: 11, color: '#fa8c16' }}>
              <Icon type="exclamation-circle" /> {n}
            </div>
          ))}
        </div>
      ),
    },
  ];

  const intervalColumns = [
    { title: 'DG', dataIndex: 'assetCode', key: 'assetCode', fixed: 'left', width: 150, sorter: strSorter('assetCode'), ...searchProps('assetCode') },
    { title: 'From full tank', dataIndex: 'previousMs', key: 'previousMs', width: 160, sorter: numSorter('previousMs'), render: (v) => moment(v).format('DD-MMM-YYYY HH:mm') },
    { title: 'To full tank', dataIndex: 'currentMs', key: 'currentMs', width: 160, sorter: numSorter('currentMs'), render: (v) => moment(v).format('DD-MMM-YYYY HH:mm') },
    { title: 'Voucher', dataIndex: 'voucherNo', key: 'voucherNo', width: 110, render: (v) => v || '—' },
    { title: 'Fuel L', dataIndex: 'fuelL', key: 'fuelL', width: 100, align: 'right', sorter: numSorter('fuelL'), render: fmtL },
    { title: 'KWH run', dataIndex: 'kwhRun', key: 'kwhRun', width: 110, align: 'right', sorter: numSorter('kwhRun'), render: fmtInt },
    { title: 'Hours run', dataIndex: 'hoursRun', key: 'hoursRun', width: 100, align: 'right', sorter: numSorter('hoursRun'), render: (v) => fmtNum(v, 2) },
    {
      title: 'Operating Level',
      dataIndex: 'avgKw',
      key: 'avgKw',
      width: 150,
      align: 'right',
      sorter: numSorter('avgKw'),
      render: (v, r) => (
        <div>
          <b>{fmtNum(v, 2)} KWH/Hour</b>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>
            {fmtNum(r.kwhRun, 0)} KWH / {fmtNum(r.hoursRun, 2)} HOURS
          </div>
        </div>
      ),
    },
    {
      title: 'Utilisation',
      dataIndex: 'utilisation',
      key: 'utilisation',
      width: 110,
      align: 'right',
      sorter: numSorter('utilisation'),
      render: (v) => withReason(v === null ? null : pct(v), 'Rating not confirmed'),
    },
    { title: 'kWh/L', dataIndex: 'efficiencyKwhPerL', key: 'efficiencyKwhPerL', width: 90, align: 'right', sorter: numSorter('efficiencyKwhPerL'), render: (v) => fmtNum(v) },
    { title: 'L/hour', dataIndex: 'litresPerHour', key: 'litresPerHour', width: 90, align: 'right', sorter: numSorter('litresPerHour'), render: (v) => fmtNum(v) },
    {
      title: 'In fuel curve',
      dataIndex: 'validForFuelCurve',
      key: 'validForFuelCurve',
      width: 140,
      filters: [{ text: 'Yes', value: true }, { text: 'No', value: false }],
      onFilter: (val, r) => String(r.validForFuelCurve) === String(val),
      render: (v, r) =>
        v ? <Tag color="green">Yes</Tag> : <Tooltip title={r.fuelCurveReason}><Tag color="orange">No</Tag></Tooltip>,
    },
  ];

  const sizeColumns = [
    { title: 'kVA', dataIndex: 'kva', key: 'kva', width: 100, align: 'right' },
    {
      title: 'Available for sizing',
      dataIndex: 'available',
      key: 'available',
      width: 160,
      render: (v, r) => (
        <Select size="small" style={{ width: 90 }} value={v ? 'yes' : 'no'} onChange={(val) => setSizeField(r.kva, 'available', val === 'yes')}>
          <Option value="yes">Yes</Option>
          <Option value="no">No</Option>
        </Select>
      ),
    },
    {
      title: 'Budgetary purchase price ₹ (optional)',
      dataIndex: 'price',
      key: 'price',
      render: (v, r) => (
        <InputNumber
          style={{ width: 180 }}
          min={0}
          step={10000}
          value={v}
          placeholder="not entered"
          onChange={(val) => setSizeField(r.kva, 'price', val)}
        />
      ),
    },
  ];

  // --- render ---------------------------------------------------------------
  const portfolio = analysis ? analysis.portfolio : null;
  const curve = analysis ? analysis.curve : null;

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <span>
            <Icon type="thunderbolt" style={{ marginRight: 8 }} />
            DG Operating Level &amp; Capacity Utilisation
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="What this measures"
          description={
            <span>
              Every DG (an asset with <b>KWH</b> defined as a meter reading) is measured full tank to full tank.
              Operating Level = KWH run ÷ Hours run = <b>average kW</b>; utilisation compares that with the rated kVA.
              The kW is an <b>interval average between refuelling events, not an instantaneous peak</b> — motor starts
              and step loads can be materially higher, so a candidate size here is an input to a load study, never a
              purchase decision on its own.
            </span>
          }
        />

        <Row gutter={12} type="flex" align="middle">
          <Col>
            <span style={{ marginRight: 8 }}>Period:</span>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="DD-MMM-YYYY"
              allowClear={false}
              ranges={{
                'Last 30 days': [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')],
                'Last 3 months': [moment().subtract(3, 'months').startOf('day'), moment().endOf('day')],
                'Last 6 months': [moment().subtract(6, 'months').startOf('day'), moment().endOf('day')],
              }}
            />
          </Col>
          <Col>
            <Button type="primary" icon="search" loading={loading} onClick={loadScope}>
              Load DGs for this period
            </Button>
          </Col>
          {scope ? (
            <Col>
              <span style={{ color: '#8c8c8c' }}>
                {scope.assets.length} DG(s) · {scope.models.length} model(s) · {scope.totalEntries} fuel entries
              </span>
            </Col>
          ) : null}
        </Row>
      </Card>

      {scope ? (
        <Card title="1. Confirm the rated kVA of each DG model" style={{ marginBottom: 16 }} size="small">
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="These ratings are parsed from names — check them before reading any utilisation figure"
            description={
              <span>
                AssetPro stores no capacity field, so the kVA below was read out of the asset model / sub-type text and
                is a <b>suggestion</b>. Correct anything that is wrong. A model left blank is still measured for
                operating level and efficiency, but is excluded from utilisation, sizing and saving — nothing is
                assumed for it. Confirmed ratings are remembered on this browser for {getServerType()}.
              </span>
            }
          />
          <Table
            columns={modelColumns}
            dataSource={scope.models}
            rowKey="modelKey"
            size="small"
            pagination={false}
            scroll={{ x: 1100 }}
          />
        </Card>
      ) : null}

      {scope ? (
        <Collapse style={{ marginBottom: 16 }}>
          <Panel header="2. Assumptions, standard sizes and prices" key="assumptions">
            <Row gutter={24}>
              <Col span={10}>
                <h4>Screening assumptions</h4>
                <p style={{ color: '#8c8c8c', fontSize: 12 }}>
                  Engineering parameters used to derive kW from kVA and to size a candidate. Every figure in the report
                  is produced with exactly these values.
                </p>
                {[
                  ['powerFactor', 'Power factor (kVA → kW)', 0.01, 0.1, 1],
                  ['targetLoading', 'Target loading at P95', 0.01, 0.1, 1],
                  ['maxPlausibleUtilisation', 'Maximum plausible utilisation', 0.05, 0.5, 3],
                  ['minPlausibleEfficiency', 'Minimum plausible kWh/L', 0.1, 0, 20],
                  ['maxPlausibleEfficiency', 'Maximum plausible kWh/L', 0.1, 0, 20],
                  ['residualTrimPercent', 'Fuel-curve residual trim %', 1, 0, 50],
                  ['minAnnualisationDays', 'Minimum days before annualising', 1, 1, 365],
                ].map(([key, label, step, min, max]) => (
                  <Row key={key} gutter={8} style={{ marginBottom: 8 }} type="flex" align="middle">
                    <Col span={14}>
                      <span style={{ fontSize: 13 }}>{label}</span>
                    </Col>
                    <Col span={10}>
                      <InputNumber
                        style={{ width: '100%' }}
                        step={step}
                        min={min}
                        max={max}
                        value={assumptions[key]}
                        onChange={(v) => (v === null || v === undefined ? null : setAssumption(key, v))}
                      />
                    </Col>
                  </Row>
                ))}
                <Row gutter={8} style={{ marginTop: 16 }} type="flex" align="middle">
                  <Col span={14}>
                    <span style={{ fontSize: 13 }}>
                      <b>Diesel price ₹/L</b>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        Commercial data with no default — money columns stay blank until entered.
                      </div>
                    </span>
                  </Col>
                  <Col span={10}>
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={1}
                      value={dieselPrice}
                      placeholder="not entered"
                      onChange={(v) => {
                        setDieselPrice(v);
                        persist({ dieselPrice: v });
                      }}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={14}>
                <h4>Standard sizes and budgetary prices</h4>
                <p style={{ color: '#8c8c8c', fontSize: 12 }}>
                  A requirement is rounded up to the smallest <b>available</b> size that carries it. Prices are
                  optional: enter the ones you have quotations for and capex/payback appear only for those sizes.
                </p>
                <Row gutter={8} style={{ marginBottom: 8 }}>
                  <Col span={10}>
                    <InputNumber style={{ width: '100%' }} min={0} value={newSize} placeholder="Add a kVA size" onChange={setNewSize} />
                  </Col>
                  <Col span={6}>
                    <Button icon="plus" onClick={addSize}>
                      Add
                    </Button>
                  </Col>
                </Row>
                <Table
                  columns={sizeColumns}
                  dataSource={sizeRows}
                  rowKey={(r) => String(r.kva)}
                  size="small"
                  pagination={{ pageSize: 10, size: 'small' }}
                />
              </Col>
            </Row>
          </Panel>
        </Collapse>
      ) : null}

      {analysis ? (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={12} type="flex" align="middle">
              <Col>
                <Select
                  mode="multiple"
                  style={{ minWidth: 180 }}
                  placeholder="Location"
                  value={filterLocations}
                  onChange={setFilterLocations}
                  maxTagCount={1}
                >
                  {options.locations.map((l) => (
                    <Option key={l} value={l}>
                      {l}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  mode="multiple"
                  style={{ minWidth: 180 }}
                  placeholder="Sub location"
                  value={filterSubLocations}
                  onChange={setFilterSubLocations}
                  maxTagCount={1}
                >
                  {options.subLocations.map((l) => (
                    <Option key={l} value={l}>
                      {l}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  mode="multiple"
                  style={{ minWidth: 200 }}
                  placeholder="Asset model"
                  value={filterModels}
                  onChange={setFilterModels}
                  maxTagCount={1}
                >
                  {options.models.map((l) => (
                    <Option key={l} value={l}>
                      {l}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Input.Search
                  style={{ width: 200 }}
                  placeholder="Asset / owner code"
                  value={assetSearch}
                  onChange={(e) => setAssetSearch(e.target.value)}
                  allowClear
                />
              </Col>
              <Col style={{ marginLeft: 'auto' }}>
                <Tooltip title="Management PDF for the loaded period with the location, sub-location, model and asset filters shown here: table, utilisation graphs and a recommended DG size per set.">
                  <Button
                    type="primary"
                    icon="file-pdf"
                    loading={exporting}
                    disabled={!analysis.assetRows.length}
                    onClick={exportPdf}
                  >
                    Export PDF
                  </Button>
                </Tooltip>
              </Col>
            </Row>
          </Card>

          <Card title="Portfolio summary" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={4}>
                <Statistic
                  title="Weighted utilisation"
                  value={portfolio.weightedUtilisation === null ? '—' : `${(portfolio.weightedUtilisation * 100).toFixed(1)}%`}
                />
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>{portfolio.assetsIncluded} DG(s) with a confirmed rating</div>
              </Col>
              <Col span={4}>
                <Statistic
                  title="Installed kVA"
                  value={portfolio.assetsIncluded === 0 ? '—' : fmtInt(portfolio.installedKva)}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Candidate kVA"
                  value={portfolio.recommendedKva === null ? '—' : fmtInt(portfolio.recommendedKva)}
                />
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                  {portfolio.capacityReduction === null
                    ? `${portfolio.assetsSized} of ${portfolio.assetsIncluded} sized`
                    : `${pct(portfolio.capacityReduction)} reduction`}
                </div>
              </Col>
              <Col span={4}>
                <Statistic
                  title="Period saving (L)"
                  value={portfolio.periodSavingL === null ? '—' : fmtInt(portfolio.periodSavingL)}
                />
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>{portfolio.periodSavingAssets} DG(s)</div>
              </Col>
              <Col span={4}>
                <Statistic
                  title="Annualised (L/yr)"
                  value={portfolio.annualisedSavingL === null ? '—' : fmtInt(portfolio.annualisedSavingL)}
                />
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>{portfolio.annualisedSavingAssets} DG(s)</div>
              </Col>
              <Col span={4}>
                <Statistic
                  title="Simple payback (months)"
                  value={portfolio.paybackMonths === null ? '—' : fmtNum(portfolio.paybackMonths, 1)}
                />
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                  {portfolio.paybackMonths === null
                    ? 'Needs prices for every replaced size'
                    : `₹${fmtInt(portfolio.replacementCostInr)} capex`}
                </div>
              </Col>
            </Row>
          </Card>

          <Tabs defaultActiveKey="perDg">
            <TabPane tab={`Per DG (${analysis.assetRows.length})`} key="perDg">
              <Card size="small" title="Utilisation over the period" style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                  One point per measured full-tank-to-full-tank interval, plotted at its closing full tank. Each point
                  is the average utilisation over that whole interval, not a reading at that moment. A line stops
                  where an interval was excluded instead of joining across it — see Data quality for why. Hover a
                  point to see its dates, vouchers, hours run, kWh, litres, kWh/L and how utilisation was worked out.
                </p>
                {analysis.utilisationSeries.noRating.length ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message={`Not plotted — no confirmed rated kVA: ${analysis.utilisationSeries.noRating.join(', ')}`}
                  />
                ) : null}
                {analysis.utilisationSeries.series.length ? null : (
                  <p style={{ color: '#8c8c8c' }}>No DG in view has a measured interval with a confirmed rating.</p>
                )}
                <div ref={chartDivRef} style={{ width: '100%', height: 380 }} />
              </Card>
              <ExportableTable
                columns={assetColumns}
                dataSource={analysis.assetRows}
                rowKey="key"
                size="small"
                scroll={{ x: 2400 }}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                exportName={`dg-operating-level-${moment().format('YYYYMMDD')}.csv`}
                exportHeader={[
                  'Asset', 'Owner code', 'Location', 'Sub location', 'Model', 'Rated kVA', 'Rated kW', 'Valid intervals',
                  'Hours', 'Energy kWh', 'Fuel L', 'Average kW', 'Utilisation', 'kWh/L', 'P95 kW', 'Required kVA',
                  'Candidate kVA', 'P95 loading after', 'Observed days', 'Period saving L', 'Annualised saving L',
                  'Replacement INR', 'Annual fuel value INR', 'Payback months', 'Action', 'Notes',
                ]}
                exportRow={(r) => [
                  r.assetCode, r.ownerCode, r.location, r.subLocation, r.assetModel, nzCsv(r.ratedKva), nzCsv(r.ratedKw),
                  r.intervalCount, nzCsv(r.hours), nzCsv(r.energyKwh), nzCsv(r.fuelL), nzCsv(r.avgKw),
                  nzCsv(r.utilisation), nzCsv(r.efficiencyKwhPerL), nzCsv(r.p95Kw), nzCsv(r.requiredKva),
                  nzCsv(r.recommendedKva), nzCsv(r.p95LoadingAfter), nzCsv(r.observedDays), nzCsv(r.periodSavingL),
                  nzCsv(r.annualisedSavingL), nzCsv(r.replacementCostInr), nzCsv(r.annualFuelValueInr),
                  nzCsv(r.paybackMonths), r.action, r.notes.join(' | '),
                ]}
              />
            </TabPane>

            <TabPane tab={`Intervals (${analysis.allIntervals.length})`} key="intervals">
              <ExportableTable
                columns={intervalColumns}
                dataSource={analysis.allIntervals}
                rowKey="key"
                size="small"
                scroll={{ x: 1700 }}
                pagination={{ pageSize: 50, showSizeChanger: true }}
                exportName={`dg-intervals-${moment().format('YYYYMMDD')}.csv`}
                exportHeader={[
                  'Asset', 'Owner code', 'Model', 'Location', 'From full tank', 'To full tank', 'Voucher', 'Fuel L',
                  'KWH run', 'Hours run', 'Operating Level KWH/Hour', 'Rated kVA', 'Utilisation', 'kWh/L', 'L/hour',
                  'In fuel curve', 'Fuel curve exclusion reason',
                ]}
                exportRow={(r) => [
                  r.assetCode, r.ownerCode, r.assetModel, r.location,
                  moment(r.previousMs).format('YYYY-MM-DD HH:mm:ss'),
                  moment(r.currentMs).format('YYYY-MM-DD HH:mm:ss'),
                  r.voucherNo, nzCsv(r.fuelL), nzCsv(r.kwhRun), nzCsv(r.hoursRun), nzCsv(r.avgKw), nzCsv(r.ratedKva),
                  nzCsv(r.utilisation), nzCsv(r.efficiencyKwhPerL), nzCsv(r.litresPerHour),
                  r.validForFuelCurve ? 'Yes' : 'No', r.fuelCurveReason || '',
                ]}
              />
            </TabPane>

            <TabPane tab="Fleet fuel curve" key="curve">
              <Card size="small" style={{ marginBottom: 16 }}>
                {curve.ok ? (
                  <div>
                    <p style={{ fontSize: 15 }}>
                      <b>
                        litres/hour = {curve.capacityCoefficient.toFixed(5)} × installed kVA +{' '}
                        {curve.loadCoefficient.toFixed(5)} × average kW
                      </b>
                    </p>
                    <p style={{ color: '#595959' }}>
                      Fitted through the origin on {curve.nFitted} interval(s) of {curve.nInput} usable
                      ({curve.nTrimmed} dropped as the largest {assumptions.residualTrimPercent}% of residuals).
                      Uncentered R² = {curve.rSquared === null ? '—' : curve.rSquared.toFixed(4)}.
                    </p>
                    <p style={{ color: '#595959' }}>
                      The first coefficient is the litres/hour a set burns for its <b>size alone</b>. Removing that
                      penalty — while holding delivered load and hours unchanged — is the entire saving claimed in this
                      report. It excludes maintenance, ageing and any generator-specific fuel curve, none of which are
                      in this data.
                    </p>
                    {!curve.capacityCoefficientUsable ? (
                      <Alert
                        type="warning"
                        showIcon
                        message="No size-related fuel penalty in this fleet"
                        description="The capacity coefficient is not positive, so this data does not show that a larger set burns more for its size. No capacity saving is claimed for any DG."
                      />
                    ) : null}
                  </div>
                ) : (
                  <Alert type="warning" showIcon message="No fleet fuel curve could be fitted" description={curve.reason} />
                )}
              </Card>
              {curve.ok ? (
                <Card size="small" title="Measured vs predicted litres/hour (dashed line = perfect fit)">
                  <div ref={curveChartDivRef} style={{ width: '100%', height: 380 }} />
                </Card>
              ) : null}
            </TabPane>

            <TabPane tab="Data quality" key="quality">
              <Row gutter={16}>
                <Col span={12}>
                  <Card size="small" title="Coverage" style={{ marginBottom: 16 }}>
                    <p>
                      {analysis.assetRows.length} DG(s) in view · {analysis.allIntervals.length} measurable
                      full-tank-to-full-tank interval(s) · {analysis.curveObservationCount} usable for the fuel curve.
                    </p>
                    {analysis.unconfirmed.length ? (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 8 }}
                        message={`${analysis.unconfirmed.length} DG(s) have no confirmed rated kVA`}
                        description={
                          <span>
                            Measured for operating level and efficiency, but excluded from utilisation, sizing, saving
                            and every portfolio total: {analysis.unconfirmed.map((r) => r.assetCode).join(', ')}
                          </span>
                        }
                      />
                    ) : null}
                    {analysis.noIntervals.length ? (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 8 }}
                        message={`${analysis.noIntervals.length} DG(s) produced no measurable interval`}
                        description={analysis.noIntervals.map((r) => r.assetCode).join(', ')}
                      />
                    ) : null}
                    {analysis.meterSuspect.length ? (
                      <Alert
                        type="error"
                        showIcon
                        message={`${analysis.meterSuspect.length} DG(s) have implausible efficiency`}
                        description={
                          <span>
                            Their kWh/L is outside {assumptions.minPlausibleEfficiency}–
                            {assumptions.maxPlausibleEfficiency} kWh/L, which points at metering rather than sizing. No
                            saving is claimed for them:{' '}
                            {analysis.meterSuspect.map((r) => `${r.assetCode} (${fmtNum(r.efficiencyKwhPerL)} kWh/L)`).join(', ')}
                          </span>
                        }
                      />
                    ) : null}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    size="small"
                    title="Intervals excluded from the load analysis"
                    extra={
                      <Button
                        size="small"
                        icon="download"
                        disabled={!analysis.dropReasons.length}
                        onClick={() =>
                          downloadCsv(
                            `dg-excluded-intervals-${moment().format('YYYYMMDD')}.csv`,
                            ['Reason', 'Count', 'Examples'],
                            analysis.dropReasons.map((d) => [d.reason, d.count, d.examples.join(' | ')])
                          )
                        }
                      >
                        CSV
                      </Button>
                    }
                  >
                    {analysis.dropReasons.length ? (
                      <Table
                        size="small"
                        pagination={false}
                        rowKey="reason"
                        dataSource={analysis.dropReasons}
                        columns={[
                          { title: 'Reason', dataIndex: 'reason', key: 'reason' },
                          { title: 'Count', dataIndex: 'count', key: 'count', width: 80, align: 'right' },
                          {
                            title: 'Examples',
                            dataIndex: 'examples',
                            key: 'examples',
                            width: 240,
                            render: (v) => <span style={{ fontSize: 11, color: '#8c8c8c' }}>{v.join(', ')}</span>,
                          },
                        ]}
                      />
                    ) : (
                      <p style={{ color: '#8c8c8c' }}>Every full-tank pair in the period was measurable.</p>
                    )}
                  </Card>
                </Col>
              </Row>

              <Card size="small" title="Method" style={{ marginTop: 16 }}>
                <ul style={{ paddingLeft: 20, color: '#595959' }}>
                  <li>
                    An interval runs between two consecutive <b>filled-to-full</b> entries. Fuel = the closing full
                    tank plus every entry in between. Meter runs use the cumulative calculated reading, so a meter
                    replacement does not create a false run.
                  </li>
                  <li>
                    Operating Level = KWH run ÷ Hours run, the same definition the Fuel Alert Analysis F2F trend uses.
                    Because it is energy over time, it <b>is</b> the average kW delivered over that interval.
                  </li>
                  <li>
                    Rated kW = confirmed kVA × power factor ({assumptions.powerFactor}). Utilisation = average kW ÷
                    rated kW. An interval above {(assumptions.maxPlausibleUtilisation * 100).toFixed(0)}% utilisation is
                    dropped as implausible rather than believed.
                  </li>
                  <li>
                    Required kVA = P95 interval-average kW ÷ (power factor × target loading{' '}
                    {assumptions.targetLoading}). P95 uses linear interpolation, the same convention as Excel's
                    PERCENTILE.INC.
                  </li>
                  <li>
                    Saving = capacity coefficient × (installed kVA − candidate kVA) × hours run. Annualised only when
                    the DG's own observed span is at least {assumptions.minAnnualisationDays} days, and scaled by that
                    span — it assumes the same duty cycle continues, which is an assumption, not a measurement.
                  </li>
                  <li>
                    Nothing is estimated to fill a gap. Missing meters, faulty-meter flags, zero hours and negative runs
                    all remove the interval with the reason recorded above; they are never treated as zero.
                  </li>
                </ul>
              </Card>
            </TabPane>
          </Tabs>
        </>
      ) : null}

      {!scope && !loading ? (
        <Card>
          <p style={{ color: '#8c8c8c' }}>
            Choose a period and load the DGs. A longer period gives more full-tank intervals and a more trustworthy P95
            load — three months or more is a reasonable starting point.
          </p>
        </Card>
      ) : null}
    </div>
  );
};

export default DGOperatingLevelReport;
